import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";

import CheckCircle from "../../../assets/images/comman/check-circle.png";
import Search from "../../../assets/images/comman/search-outline.svg";
import {
  getAllPatients,
  getAvailableTestTypes,
  getLabTestParameters,
  upsertLabReport,
  getLabReportByVisit,
  getPatientById,
  addVisit,
} from "../../../api/apiService";
import ToastContainer, {
  useToast,
} from "../../../components/common/ToastContainer";
import LinkAbhaButton from "../Patients/LinkAbhaButton";
import { getAgoTime } from "../Patients/utils";
import {
  formatPatientName,
  calculateAge,
  formatDobDisplay,
} from "../../../utils/patientVisitUtils";
import { sanitizeText } from "../../../utils/inputValidation";
import { useAuth } from "../../../context/AuthContext";
import { PERMISSIONS } from "../../../constants/permissions";

const CreateLabReports = () => {
  const location = useLocation();
  const { canAny, user } = useAuth();
  const canWriteLab = canAny(
    PERMISSIONS.LAB_ENTER_RESULTS,
    PERMISSIONS.LAB_FINALIZE
  );
  const initialPatient =
    location.state?.selectedPatient ||
    location.state?.patientData ||
    null;

  const [selectedPatient, setSelectedPatient] = useState(initialPatient);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [patientsLoading, setPatientsLoading] = useState(true);
  const [patientDetailsLoading, setPatientDetailsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showPatientList, setShowPatientList] = useState(
    !initialPatient && !location.state?.patientId && !new URLSearchParams(location.search).get("patientId")
  );
  const [totalPatients, setTotalPatients] = useState(0);
  const navigate = useNavigate();

  // Dynamic test types & params state
  const [testTypes, setTestTypes] = useState([]);
  const [selectedTestType, setSelectedTestType] = useState("");
  const [testTemplate, setTestTemplate] = useState(null);
  const [dynamicParams, setDynamicParams] = useState({});
  const [existingLabReport, setExistingLabReport] = useState(null);

  // Toast functionality
  const { toasts, removeToast, showSuccess, showError, showInfo } =
    useToast();

  // Get today's date in YYYY-MM-DD format for max date restriction
  const getTodaysDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  // Fetch patients data and test types from API
  useEffect(() => {
    const initData = async () => {
      try {
        const typesRes = await getAvailableTestTypes();
        // Handle various possible backend response wrappers
        let typesList = typesRes?.data || typesRes;

        // If the backend isn't ready or returned empty, use mock data so selection works
        if (!typesList || !Array.isArray(typesList) || typesList.length === 0) {
          console.warn(
            "API returned empty/invalid test types. Using fallback data for demo.",
          );
          typesList = [
            { testType: "blood", displayName: "Blood Test", uiType: "table" },
            {
              testType: "urine",
              displayName: "Urine Test",
              uiType: "grouped_form",
            },
            {
              testType: "glucose",
              displayName: "Glucose Test",
              uiType: "simple_form",
            },
            {
              testType: "lipid",
              displayName: "Lipid Profile",
              uiType: "table",
            },
          ];
        }
        setTestTypes(typesList);
      } catch (err) {
        console.error(
          "Failed to load test types from API, using fallback data:",
          err,
        );
        setTestTypes([
          { testType: "blood", displayName: "Blood Test", uiType: "table" },
          {
            testType: "urine",
            displayName: "Urine Test",
            uiType: "grouped_form",
          },
          {
            testType: "glucose",
            displayName: "Glucose Test",
            uiType: "simple_form",
          },
          { testType: "lipid", displayName: "Lipid Profile", uiType: "table" },
        ]);
      }
    };
    initData();

    const fetchPatients = async () => {
      try {
        setPatientsLoading(true);
        const response = await getAllPatients();

        if (response.success) {
          // Transform API data to match component structure
          const transformedPatients = response.data.patients.map((patient) => {
            const rawName =
              patient.name && typeof patient.name === "string" && patient.name.trim() && patient.name.trim() !== "undefined undefined"
                ? patient.name.trim()
                : patient.fullName && typeof patient.fullName === "string" && patient.fullName.trim() && patient.fullName.trim() !== "undefined undefined"
                ? patient.fullName.trim()
                : formatPatientName(patient) || "Patient";
            const fullName = rawName;

            const dobFormatted = formatDobDisplay(patient.dob);

            let computedAge = "";
            if (dobFormatted) {
              const calc = calculateAge(dobFormatted);
              if (calc && typeof calc === "number" && !isNaN(calc) && calc > 0 && calc < 130) {
                computedAge = calc;
              }
            }
            if (!computedAge && patient.age !== undefined && patient.age !== null && patient.age !== "") {
              const parsed = parseInt(String(patient.age).replace(/[^0-9]/g, ""), 10);
              if (!isNaN(parsed) && parsed > 0 && parsed < 130) {
                computedAge = parsed;
              }
            }

            const visitTimestamp =
              patient.lastVisitDate ||
              patient.time ||
              patient.appointmentDate ||
              patient.updatedAt ||
              patient.lastVisit ||
              patient.createdAt;

            const lastVisit = formatDate(visitTimestamp);
            const lastVisitAgo = visitTimestamp ? getTimeAgo(visitTimestamp) : "";

            // Helper function to safely get value or return empty string
            const safeGet = (value) => value || "";

            const isAbhaLinked = Boolean(
              patient.isAbhaLinked === true ||
              patient.isabhalinked === true ||
              patient.isABHALinked === true ||
              String(patient.isAbhaLinked).toLowerCase() === "true" ||
              String(patient.isabhalinked).toLowerCase() === "true" ||
              String(patient.isABHALinked).toLowerCase() === "true" ||
              (patient.maskedAbha && String(patient.maskedAbha).trim() && patient.maskedAbha !== "N/A") ||
              (patient.ABHANumber && String(patient.ABHANumber).trim() && patient.ABHANumber !== "N/A") ||
              (patient.abhaNumber && String(patient.abhaNumber).trim() && patient.abhaNumber !== "N/A") ||
              (patient.abhaaddress && String(patient.abhaaddress).trim()) ||
              (patient.abhaAddress && String(patient.abhaAddress).trim()) ||
              (patient.ABHAAddress && String(patient.ABHAAddress).trim())
            );

            const maskedAbha =
              patient.maskedAbha ||
              (patient.ABHANumber ? `**** ${String(patient.ABHANumber).slice(-4)}` : "") ||
              patient.formattedAbhaId ||
              (patient.abhaaddress || patient.abhaAddress || patient.ABHAAddress || "");

            return {
              id: patient._id || patient.id,
              _id: patient._id || patient.id,
              name: fullName,
              fullName: fullName,
              age: computedAge ? `${computedAge}yr` : "",
              calculatedAge: computedAge,
              dob: dobFormatted,
              isAbhaLinked,
              isabhalinked: isAbhaLinked,
              maskedAbha,
              abhaId: maskedAbha || (isAbhaLinked ? "Linked" : "N/A"),
              formattedAbhaId: maskedAbha || patient.ABHANumber || "",
              lastVisit,
              lastVisitAgo,
              opdId:
                safeGet(patient.opdId) || (patient._id || patient.id || "").slice(-8).toUpperCase(),
              queue:
                patient.queue !== undefined && patient.queue !== null
                  ? String(patient.queue).padStart(2, "0")
                  : "—",
              uhid:
                safeGet(patient.uhid || patient.UHID) ||
                (patient._id || patient.id || "").slice(-8).toUpperCase(),
              fee:
                safeGet(
                  patient.fee !== undefined && patient.fee !== ""
                    ? (typeof patient.fee === "number" ? `₹${patient.fee}` : patient.fee)
                    : patient.consultationFee
                    ? `₹${patient.consultationFee}`
                    : "",
                ) || "",
              time: safeGet(patient.time || patient.appointmentTime || "") || "",
              date: formatDate(patient.lastVisitDate || patient.appointmentDate || patient.time || new Date()),
              doctor:
                safeGet(
                  patient.doctor ||
                  patient.consultingDoctor ||
                  patient.assignedDoctor ||
                  patient.lastVisitedDoctor ||
                  patient?.visits?.[0]?.doctor,
                ) || "",
              mobile: safeGet(patient.mobile),
              address: safeGet(patient.address),
              pincode: safeGet(patient.pincode),
              status: safeGet(patient.status),
              // Store all raw patient data for lab report creation
              rawData: {
                ...patient,
                name: fullName,
                fullName: fullName,
                calculatedAge: computedAge,
                age: computedAge ? `${computedAge}yr` : "",
                isAbhaLinked,
                isabhalinked: isAbhaLinked,
                maskedAbha,
                formattedAbhaId: maskedAbha || patient.ABHANumber || "",
                ABHANumber: safeGet(patient.ABHANumber),
                ABHAAddress: safeGet(
                  patient.ABHAAddress ||
                    patient.abhaAddress ||
                    patient.abhaaddress,
                ),
                yearOfBirth:
                  patient.yearOfBirth ||
                  (patient.dob ? new Date(patient.dob).getFullYear() : null),
                gender: safeGet(patient.gender),
                dateOfBirth: safeGet(patient.dob),
                email: safeGet(patient.email),
              },
            };
          });

          setPatients(transformedPatients);
          setTotalPatients(response.data.total);
        } else {
          setError("Failed to fetch patients data");
        }
      } catch (err) {
        console.error("Error fetching patients:", err);
        setError(err.message || "Failed to fetch patients data");
      } finally {
        setPatientsLoading(false);
      }
    };

    fetchPatients();
  }, []);

  // Helper function to format date
  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    const options = { day: "2-digit", month: "short", year: "numeric" };
    return date.toLocaleDateString("en-GB", options);
  };

  // Helper function to get time ago
  const getTimeAgo = (dateString) => getAgoTime(dateString);

  // Function to load complete patient details, resolve visitId, and prefill existing lab reports
  const loadFullPatientAndVisit = async (patientOrId, initialVisitId = "") => {
    const targetId =
      (typeof patientOrId === "string" ? patientOrId : null) ||
      patientOrId?._id ||
      patientOrId?.id ||
      patientOrId?.rawData?._id ||
      patientOrId?.rawData?.id ||
      location.state?.patientId ||
      new URLSearchParams(location.search).get("patientId") ||
      "";

    if (!targetId) return;

    // Reset previous patient's test selection, template, dynamic params, and existing report!
    setSelectedPatient(null);
    setSelectedTestType("");
    setTestTemplate(null);
    setDynamicParams({});
    setExistingLabReport(null);
    setPatientDetailsLoading(true);

    // Clear form inputs
    const formElements = document.querySelectorAll("[data-field]");
    formElements.forEach((element) => {
      if (element.getAttribute("data-field") === "patient_name") return;
      if (element.type === "checkbox" || element.type === "radio") {
        element.checked = false;
      } else {
        element.value = "";
      }
    });

    let fullPatient = typeof patientOrId === "object" ? { ...patientOrId } : {};

    try {
      const fetched = await getPatientById(targetId);
      if (fetched && typeof fetched === "object") {
        fullPatient = {
          ...fullPatient,
          ...fetched,
          _id: fetched._id || fetched.id || targetId,
          id: fetched._id || fetched.id || targetId,
        };
      }
    } catch (err) {
      console.warn("Could not load full patient profile via getPatientById:", err);
    }

    // Resolve visitId from navigation state, query param, or fetched patient visits
    let resolvedVisitId =
      initialVisitId ||
      fullPatient.visitId ||
      fullPatient.visits?.[0]?.visitId ||
      fullPatient.visits?.[0]?._id ||
      fullPatient.visits?.[0]?.id ||
      fullPatient.rawData?.visitId ||
      fullPatient.rawData?.visits?.[0]?.visitId ||
      fullPatient.rawData?.visits?.[0]?._id ||
      "";

    // If patient has no visit in record, auto-create a visit so lab report can be saved!
    if (!resolvedVisitId && targetId) {
      try {
        const newVisitRes = await addVisit(targetId, {
          visitType: fullPatient.visitType || "General OPD",
          department: fullPatient.department || "Laboratory",
          doctor: fullPatient.doctor || "Lab Technician",
        });
        const createdVisit =
          newVisitRes?.data?.visits?.[0] ||
          newVisitRes?.visit ||
          newVisitRes?.data;
        resolvedVisitId =
          newVisitRes?.visitId ||
          createdVisit?.visitId ||
          createdVisit?._id ||
          createdVisit?.id ||
          "";
      } catch (vErr) {
        console.warn("Could not auto-create visit for lab report:", vErr);
      }
    }

    const rawName =
      (fullPatient.name && typeof fullPatient.name === "string" && fullPatient.name.trim() && fullPatient.name.trim() !== "undefined undefined")
        ? fullPatient.name.trim()
        : (fullPatient.fullName && typeof fullPatient.fullName === "string" && fullPatient.fullName.trim() && fullPatient.fullName.trim() !== "undefined undefined")
        ? fullPatient.fullName.trim()
        : formatPatientName(fullPatient) || "Patient";

    const dobFormatted = formatDobDisplay(fullPatient.dob || fullPatient.dateOfBirth);
    let computedAge = "";
    if (dobFormatted) {
      const calc = calculateAge(dobFormatted);
      if (calc && typeof calc === "number" && !isNaN(calc) && calc > 0 && calc < 130) {
        computedAge = calc;
      }
    }
    if (!computedAge && fullPatient.age !== undefined && fullPatient.age !== null && fullPatient.age !== "") {
      const parsed = parseInt(String(fullPatient.age).replace(/[^0-9]/g, ""), 10);
      if (!isNaN(parsed) && parsed > 0 && parsed < 130) {
        computedAge = parsed;
      }
    }

    const enrichedPatient = {
      ...fullPatient,
      id: targetId,
      _id: targetId,
      name: rawName,
      fullName: rawName,
      age: computedAge ? `${computedAge}yr` : (fullPatient.age ? `${fullPatient.age}yr` : ""),
      calculatedAge: computedAge,
      dob: dobFormatted,
      uhid: fullPatient.uhid || fullPatient.UHID || targetId.slice(-8).toUpperCase(),
      visitId: resolvedVisitId,
      mobile: fullPatient.mobile || fullPatient.phone || "",
      rawData: {
        ...(fullPatient.rawData || {}),
        ...fullPatient,
        _id: targetId,
        id: targetId,
        visitId: resolvedVisitId,
        visits: fullPatient.visits || [],
        ABHANumber: fullPatient.ABHANumber || fullPatient.abhaNumber || "",
        ABHAAddress: fullPatient.ABHAAddress || fullPatient.abhaAddress || fullPatient.abhaaddress || "",
      },
    };

    setSelectedPatient(enrichedPatient);
    setShowPatientList(false);
    setPatientDetailsLoading(false);

    // Prefill existing lab report for this visit
    if (resolvedVisitId) {
      try {
        const res = await getLabReportByVisit(resolvedVisitId);
        const report = res?.data || res;
        const tests = Array.isArray(report) ? report : (report?.tests || []);
        if (tests.length > 0) {
          setExistingLabReport(Array.isArray(report) ? { tests: report } : report);
          showInfo("Existing lab data loaded for this visit.");
        } else {
          setExistingLabReport(null);
        }
      } catch (err) {
        console.log("No existing lab report for visit:", err.message);
        setExistingLabReport(null);
      }
    } else {
      setExistingLabReport(null);
    }
  };

  // Load full patient when location state/params change
  useEffect(() => {
    const navPatient =
      location.state?.selectedPatient ||
      location.state?.patientData ||
      location.state?.patientId ||
      new URLSearchParams(location.search).get("patientId");
    const navVisitId =
      location.state?.visitId ||
      new URLSearchParams(location.search).get("visitId");

    if (navPatient) {
      loadFullPatientAndVisit(navPatient, navVisitId);
    }
  }, [location.state, location.search]);

  // Function to handle patient selection from table
  const handlePatientSelect = async (patient) => {
    navigate("/create-lab-report", { replace: true, state: {} });
    await loadFullPatientAndVisit(patient);
  };

  const collectFormData = () => {
    const formData = {};

    // Get all form elements with data-field attributes
    const formElements = document.querySelectorAll("[data-field]");

    formElements.forEach((element) => {
      const fieldName = element.getAttribute("data-field");
      let fieldValue = "";

      if (element.type === "checkbox") {
        fieldValue = element.checked;
      } else if (element.type === "radio") {
        if (element.checked) {
          fieldValue = element.value;
        }
      } else {
        fieldValue = element.value;
      }

      // Include all fields, even if empty (API requires some fields even if empty)
      formData[fieldName] = fieldValue;
    });

    return formData;
  };

  const handleTestTypeChange = async (e) => {
    const type = e.target.value;
    setSelectedTestType(type);
    setTestTemplate(null);
    setDynamicParams({});

    const setReportFields = (testObj) => {
      const setVal = (field, val) => {
        const el = document.querySelector(`[data-field="${field}"]`);
        if (el) el.value = val ?? "";
      };
      if (testObj) {
        setVal("sample_id", testObj.sampleId || testObj.sample_id || "");
        setVal("equipment_id", testObj.equipmentId || testObj.equipment_id || "");
        const rDate = testObj.reportDate || testObj.report_date;
        if (rDate) {
          setVal("report_date", String(rDate).split("T")[0]);
        } else {
          setVal("report_date", getTodaysDate());
        }
        const rTime = testObj.reportTime || testObj.report_time;
        if (rTime) {
          setVal("report_time", String(rTime).substring(0, 5));
        } else if (rDate && String(rDate).includes("T")) {
          const tPart = String(rDate).split("T")[1];
          if (tPart) setVal("report_time", tPart.substring(0, 5));
          else setVal("report_time", "");
        } else {
          setVal("report_time", "");
        }
        setVal("analyst_name", testObj.analystName || testObj.analyst_name || "");
        setVal(
          "additional_observations",
          testObj.observations || testObj.additional_observations || ""
        );
        setVal("equipment_status", testObj.equipmentStatus || testObj.equipment_status || "");
        const cTime = testObj.captureTime || testObj.capture_time;
        if (cTime) {
          setVal("capture_time", String(cTime).split("T")[0]);
        } else {
          setVal("capture_time", "");
        }
      } else {
        setVal("sample_id", "");
        setVal("equipment_id", "");
        setVal("report_date", getTodaysDate());
        setVal("report_time", "");
        setVal("analyst_name", "");
        setVal("additional_observations", "");
        setVal("equipment_status", "");
        setVal("capture_time", "");
      }
    };

    if (!type) {
      setReportFields(null);
      return;
    }

    const tests = Array.isArray(existingLabReport)
      ? existingLabReport
      : existingLabReport?.tests || [];
    const existingTest = tests.find((t) => t.testType === type);
    setReportFields(existingTest);

    setLoading(true);
    try {
      const res = await getLabTestParameters(type);
      let testData = res?.data || res;

      // Fallback if API fails or is not implemented yet
      if (!testData || Object.keys(testData).length === 0) {
        throw new Error("Empty param data");
      }

      setTestTemplate(testData);
      const initParams = {};

      testData.parameters.forEach((p) => {
        const existingParam = existingTest?.parameters?.find(
          (ep) => ep.parameterName === p.name,
        );
        initParams[p.name] = {
          parameterName: p.name,
          parameterValue:
            existingParam?.parameterValue ||
            p.parameterValue ||
            p.value ||
            p.defaultValue ||
            "",
          unit: existingParam?.unit || p.unit || "",
          referenceRange:
            existingParam?.referenceRange || p.referenceRange || "",
          section: existingParam?.section || p.section || "",
        };
      });
      setDynamicParams(initParams);
    } catch (err) {
      console.warn(
        "Failed to fetch test parameters via API, using fallback",
        err,
      );
      // Fallback mock data based on test type
      let mockParams = [];
      let uiType = "table";
      let displayName = type.toUpperCase() + " TEST";

      if (type === "blood") {
        mockParams = [
          {
            name: "Hemoglobin (Hb)",
            unit: "g/dL",
            referenceRange: "13-17",
            inputType: "number",
          },
          {
            name: "WBC",
            unit: "/uL",
            referenceRange: "4000-11000",
            inputType: "number",
          },
        ];
        displayName = "Blood Test";
      } else if (type === "urine") {
        mockParams = [
          {
            name: "Color",
            inputType: "select",
            options: ["Yellow", "Pale", "Dark"],
          },
          { name: "pH", referenceRange: "4.5-8.0", inputType: "number" },
        ];
        uiType = "grouped_form";
        displayName = "Urine Test";
      } else if (type === "glucose") {
        mockParams = [
          {
            name: "Fasting Blood Sugar",
            unit: "mg/dL",
            referenceRange: "70-100",
            inputType: "number",
          },
        ];
        uiType = "simple_form";
        displayName = "Glucose Test";
      } else {
        mockParams = [
          { name: "Result Value", unit: "units", inputType: "text" },
        ];
      }

      const mockData = {
        testType: type,
        displayName,
        uiType,
        parameters: mockParams,
      };

      setTestTemplate(mockData);
      const initParams = {};
      mockParams.forEach((p) => {
        const existingParam = existingTest?.parameters?.find(
          (ep) => ep.parameterName === p.name,
        );
        initParams[p.name] = {
          parameterName: p.name,
          parameterValue:
            existingParam?.parameterValue ||
            p.parameterValue ||
            p.value ||
            p.defaultValue ||
            "",
          unit: existingParam?.unit || p.unit || "",
          referenceRange:
            existingParam?.referenceRange || p.referenceRange || "",
          section: existingParam?.section || p.section || "",
        };
      });
      setDynamicParams(initParams);
    } finally {
      setLoading(false);
    }
  };

  const handleParamChange = (name, value) => {
    setDynamicParams((prev) => ({
      ...prev,
      [name]: { ...prev[name], parameterValue: value },
    }));
  };

  const handleSaveLabReport = async (e) => {
    if (e) e.preventDefault();
    if (!canWriteLab) {
      showError("You do not have permission to enter or save lab results.");
      return;
    }
    if (!selectedPatient) {
      showError("No patient selected. Please select a patient first.");
      return;
    }

    let visitId =
      selectedPatient.visitId ||
      selectedPatient.rawData?.visitId ||
      selectedPatient.rawData?.visits?.[0]?.visitId ||
      selectedPatient.rawData?.visits?.[0]?._id ||
      selectedPatient.visits?.[0]?.visitId ||
      selectedPatient.visits?.[0]?._id ||
      selectedPatient.visits?.[0]?.id ||
      "";
    const patientId =
      selectedPatient.id ||
      selectedPatient._id ||
      selectedPatient.rawData?._id ||
      selectedPatient.rawData?.id ||
      "";

    // Auto-create visit on the fly if still missing
    if (!visitId && patientId) {
      try {
        const newVisitRes = await addVisit(patientId, {
          visitType: selectedPatient.visitType || "General OPD",
          department: selectedPatient.department || "Laboratory",
          doctor: selectedPatient.doctor || "Lab Technician",
        });
        const createdVisit =
          newVisitRes?.data?.visits?.[0] ||
          newVisitRes?.visit ||
          newVisitRes?.data;
        visitId =
          newVisitRes?.visitId ||
          createdVisit?.visitId ||
          createdVisit?._id ||
          createdVisit?.id ||
          "";
        if (visitId) {
          setSelectedPatient((prev) => ({
            ...prev,
            visitId,
            rawData: { ...(prev?.rawData || {}), visitId },
          }));
        }
      } catch (vErr) {
        console.warn("Fallback visit creation failed on save:", vErr);
      }
    }

    if (!visitId) {
      showError("Visit ID is missing. Could not auto-assign visit. Please ensure patient has an active visit.");
      return;
    }

    try {
      setLoading(true);

      const formData = collectFormData();
      const paramsList = Object.values(dynamicParams).filter(
        (p) => p.parameterValue.trim() !== "",
      );

      const payload = {
        patientId: patientId,
        visitId: visitId || undefined,
        sampleId: sanitizeText(formData.sample_id),
        testType: sanitizeText(selectedTestType),
        reportDate: formData.report_date,
        analystName: sanitizeText(formData.analyst_name),
        equipmentId: sanitizeText(formData.equipment_id),
        equipmentStatus: formData.equipment_status,
        observations: sanitizeText(formData.additional_observations),
        parameters: paramsList.map((p) => ({
          ...p,
          parameterName: sanitizeText(p.parameterName),
          parameterValue: sanitizeText(p.parameterValue),
          unit: sanitizeText(p.unit),
          referenceRange: sanitizeText(p.referenceRange),
          remarks: sanitizeText(p.remarks),
        })),
      };

      let saveResponse;
      try {
        saveResponse = await upsertLabReport(payload);
      } catch (err) {
        const msg =
          err.response?.data?.message ||
          err.message ||
          "Failed to save lab results.";
        showError(msg);
        setLoading(false);
        return;
      }

      if (
        saveResponse?.status === "success" ||
        saveResponse?.success !== false
      ) {
        // Update existing lab report state with the returned centralized record
        const updatedReport = saveResponse?.data || saveResponse;
        if (updatedReport && updatedReport.tests) {
          setExistingLabReport(updatedReport);
        }
        showSuccess(saveResponse?.message || "Lab report saved successfully!");
        setTimeout(() => {
          handleCreateNewReport();
        }, 2000);
      } else {
        showError(saveResponse?.message || "Failed to save lab results.");
      }
    } catch (error) {
      console.error("Error saving lab report:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to save lab report. Please try again.";
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNewReport = () => {
    setShowSuccessModal(false);
    setSelectedPatient(null);
    setShowPatientList(true);
    setSelectedTestType("");
    setTestTemplate(null);
    setDynamicParams({});
    setExistingLabReport(null);
    // Clear form fields
    const formElements = document.querySelectorAll("[data-field]");
    formElements.forEach((element) => {
      if (element.type === "checkbox" || element.type === "radio") {
        element.checked = false;
      } else {
        element.value = "";
      }
    });
  };

  const handleViewReport = () => {
    setShowSuccessModal(false);
    const visitId =
      selectedPatient?.visitId ||
      selectedPatient?.rawData?.visitId ||
      selectedPatient?.rawData?.visits?.[0]?.visitId ||
      "";
    navigate("/view-create-lab-report", {
      state: {
        reportId: existingLabReport?._id,
        visitId: visitId,
      },
    });
  };

  const handleBackToPatients = () => {
    setSelectedPatient(null);
    setShowPatientList(true);
    setSelectedTestType("");
    setTestTemplate(null);
    setDynamicParams({});
    setExistingLabReport(null);
    navigate("/create-lab-report", { replace: true, state: {} });
    const formElements = document.querySelectorAll("[data-field]");
    formElements.forEach((element) => {
      if (element.getAttribute("data-field") === "patient_name") return;
      if (element.type === "checkbox" || element.type === "radio") {
        element.checked = false;
      } else {
        element.value = "";
      }
    });
  };

  // Show patient list if no patient is selected
  if (showPatientList) {
    return (
      <>
        <style>
          {`
                        .patient-row-hover:hover {
                            background-color: #f8f9fa !important;
                            transition: background-color 0.2s ease;
                        }
                        .patient-row-hover:hover td {
                            background-color: inherit !important;
                        }
                    `}
        </style>
        <div className="container-fluid">
          <div className="opd-page">
            <div className="opd-filter">
              <div className="opd-filter-left">
                <div className="feilds">
                  <label className="label">Department</label>
                  <select>
                    <option>All</option>
                  </select>
                </div>
                <div className="feilds">
                  <label className="label">Status</label>
                  <select>
                    <option>All</option>
                  </select>
                </div>
              </div>
              <div className="opd-filter-right">
                <div className="feilds">
                  <div className="searchBar">
                    <input
                      type="text"
                      placeholder="Search by Name, ID, or Condition"
                    />
                    <span className="searchIcon">
                      <img src={Search} alt="Search" />
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className="tabsOpdSection">
              <div className="tabsOpdSectionLeft">
                {/* <h4>Select Patient for Lab Report</h4> */}
              </div>
              <div className="tabsOpdSectionReight">
                <button
                  className="customeBtn btnEmpty me-2"
                  onClick={() => window.location.reload()}
                  disabled={patientsLoading}
                  style={{ textDecoration: "none" }}
                >
                  {patientsLoading ? "Refreshing..." : "↻ Refresh"}
                </button>
              </div>
            </div>

            <div className="OpdBodySection">
              <div className="opdAllTable">
                <table className="table table-bordered align-middle">
                  <thead>
                    <tr>
                      <th>Patient Name</th>
                      <th>OPD ID</th>
                      <th>ABHA ID</th>
                      <th>UHID</th>
                      <th>Mobile</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {patientsLoading ? (
                      <tr>
                        <td colSpan="6" className="text-center py-4">
                          <div
                            className="spinner-border text-primary"
                            role="status"
                          >
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          <p className="mt-2 mb-0">Loading patients...</p>
                        </td>
                      </tr>
                    ) : error ? (
                      <tr>
                        <td colSpan="6" className="text-center py-4">
                          <div className="alert alert-danger mb-0">
                            <strong>Error:</strong> {error}
                            <br />
                            <button
                              className="btn btn-sm btn-outline-primary mt-2"
                              onClick={() => window.location.reload()}
                            >
                              Retry
                            </button>
                          </div>
                        </td>
                      </tr>
                    ) : patients.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="text-center py-4">
                          <div className="alert alert-info mb-0">
                            <strong>No patients found.</strong>
                            {/* <br />
                            <button className="btn btn-sm btn-primary mt-2">
                              Add First Patient
                            </button> */}
                          </div>
                        </td>
                      </tr>
                    ) : (
                      patients.map((patient, index) => (
                        <tr
                          key={patient.id || index}
                          style={{ cursor: "pointer" }}
                          onClick={() => handlePatientSelect(patient)}
                          title="Click to create lab report for this patient"
                          className="patient-row-hover"
                        >
                          <td>
                            <div className="patientDetails">
                              <h5>{patient.name}</h5>
                              <p>
                                {patient.age ? `Age: ${patient.age}` : ""}
                                {patient.dob ? `${patient.age ? " | " : ""}DOB: ${patient.dob}` : ""}
                              </p>
                              {patient.lastVisit ? (
                                <p>
                                  Last Visit: {patient.lastVisit}
                                  {patient.lastVisitAgo ? ` (${patient.lastVisitAgo})` : ""}
                                </p>
                              ) : null}
                            </div>
                          </td>
                          <td>
                            <h5>{patient.opdId}</h5>
                          </td>
                          <td onClick={(e) => e.stopPropagation()}>
                            {patient.isAbhaLinked || (patient.abhaId && patient.abhaId !== "N/A") ? (
                              <h5 style={{ fontFamily: "monospace", margin: 0 }}>
                                {patient.maskedAbha || patient.abhaId || "Linked"}
                              </h5>
                            ) : (
                              <LinkAbhaButton patient={patient} />
                            )}
                          </td>
                          <td>
                            <h5>{patient.uhid}</h5>
                          </td>
                          <td>
                            <h5>{patient.mobile || "N/A"}</h5>
                          </td>
                          <td>
                            <button
                              className="customeBtn btnLightBlue"
                              onClick={(e) => {
                                e.stopPropagation();
                                handlePatientSelect(patient);
                              }}
                            >
                              Select
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
                {/* Show total patients count */}
                {!patientsLoading && !error && patients.length > 0 && (
                  <div className="mt-3 text-muted">
                    <small>Total Patients: {totalPatients}</small>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  if (patientDetailsLoading && (!selectedPatient || !selectedPatient.name)) {
    return (
      <div className="container-fluid">
        <div className="text-center p-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-2 text-secondary">Fetching complete patient profile and active visit...</p>
        </div>
      </div>
    );
  }

  if (!selectedPatient) {
    return (
      <div className="container-fluid">
        <div className="text-center p-5">
          <div className="spinner-border" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p className="mt-2">Loading patient information...</p>
        </div>
      </div>
    );
  }
  return (
    <>
      <div className="container-fluid">
        <div className="creat-lab-page py-2 px-2">
          {!canWriteLab && (
            <>
              <style>{`
                .allMainFeilds input, .allMainFeilds select, .allMainFeilds textarea {
                  background-color: #f8f9fa !important;
                  cursor: not-allowed !important;
                  color: #495057 !important;
                }
              `}</style>
              <div
                className="alert alert-info py-2 px-3 mb-3 d-flex align-items-center gap-2"
                role="alert"
                style={{
                  borderRadius: "8px",
                  fontSize: "13px",
                  backgroundColor: "#EBF5FF",
                  color: "#1E429F",
                  border: "1px solid #B4C6FC",
                }}
              >
                <i className="material-icons" style={{ fontSize: "18px" }}>visibility</i>
                <span><strong>Read-Only Mode:</strong> You do not have permission to enter or save lab results. Report details are locked in view-only mode.</span>
              </div>
            </>
          )}
          <form
            key={selectedPatient?._id || selectedPatient?.id || "create-lab-form"}
            onSubmit={handleSaveLabReport}
          >
            <div className="allMainFeilds">
              <div className="basicDetails">
                {/* Patient Info Header */}
                <div className="row mb-3">
                  <div className="col-12">
                    <div
                      className="card shadow-sm border-0 mb-2"
                      style={{
                        borderRadius: "10px",
                        background: "#f8fafc",
                        border: "1px solid #e2e8f0",
                      }}
                    >
                      <div className="card-body py-3 px-4">
                        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                          <div>
                            <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                              <h4
                                className="mb-0 fw-bold text-dark"
                                style={{ fontSize: "18px" }}
                              >
                                {selectedPatient.fullName ||
                                  selectedPatient.name ||
                                  "Patient"}
                              </h4>
                              {selectedPatient.gender && (
                                <span
                                  className="badge bg-secondary text-capitalize"
                                  style={{ fontSize: "11px" }}
                                >
                                  {selectedPatient.gender}
                                </span>
                              )}
                              {selectedPatient.age && (
                                <span
                                  className="badge bg-light text-dark border"
                                  style={{ fontSize: "11px" }}
                                >
                                  {selectedPatient.age}
                                </span>
                              )}
                              {selectedPatient.visitId ? (
                                <span
                                  className="badge bg-success"
                                  style={{ fontSize: "11px" }}
                                  title="Active Visit ID"
                                >
                                  Visit: {selectedPatient.visitId}
                                </span>
                              ) : (
                                <span
                                  className="badge bg-warning text-dark"
                                  style={{ fontSize: "11px" }}
                                >
                                  Visit: Auto-assigning...
                                </span>
                              )}
                            </div>
                            <div
                              className="d-flex align-items-center flex-wrap gap-3 text-secondary"
                              style={{ fontSize: "13px" }}
                            >
                              <span>
                                <strong>UHID:</strong>{" "}
                                {selectedPatient.uhid ||
                                  selectedPatient.id ||
                                  "N/A"}
                              </span>
                              {selectedPatient.dob && (
                                <span>
                                  <strong>DOB:</strong>{" "}
                                  {typeof selectedPatient.dob === "string"
                                    ? selectedPatient.dob.split("T")[0]
                                    : ""}
                                </span>
                              )}
                              {selectedPatient.mobile && (
                                <span>
                                  <strong>Phone:</strong> {selectedPatient.mobile}
                                </span>
                              )}
                              <span>
                                <strong>ABHA:</strong>{" "}
                                {selectedPatient.maskedAbha ||
                                  selectedPatient.abhaNumber ||
                                  selectedPatient.rawData?.ABHANumber ||
                                  "N/A"}
                                {(selectedPatient.abhaAddress ||
                                  selectedPatient.rawData?.ABHAAddress ||
                                  selectedPatient.rawData?.abhaAddress) && (
                                  <span className="ms-1 text-muted">
                                    (
                                    {selectedPatient.abhaAddress ||
                                      selectedPatient.rawData?.ABHAAddress ||
                                      selectedPatient.rawData?.abhaAddress}
                                    )
                                  </span>
                                )}
                              </span>
                            </div>
                          </div>
                          <button
                            type="button"
                            className="customeBtn btnEmpty py-1 px-3"
                            style={{ height: "36px", fontSize: "13px" }}
                            onClick={handleBackToPatients}
                          >
                            ← Switch Patient
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <fieldset
                  disabled={!canWriteLab}
                  style={{
                    border: "none",
                    padding: 0,
                    margin: 0,
                    pointerEvents: !canWriteLab ? "none" : "auto",
                    opacity: !canWriteLab ? 0.88 : 1,
                  }}
                >
                <div className="row">
                  <div className="col-xs-12 col-md-8">
                    <h4>Automated Data Capture</h4>
                    <div
                      className="patientsFeildsRow"
                      style={{ border: "0", padding: "0" }}
                    >
                      <div className="row">
                        <div className="col-xs-12 col-md-6">
                          <div className="feilds">
                            <label className="label">Equipment ID </label>
                            <input
                              type="text"
                              data-field="equipment_id"
                              required
                            />
                          </div>
                        </div>
                        <div className="col-xs-12 col-md-6">
                          <div className="feilds">
                            <label className="label">Test Type *</label>
                            <select
                              data-field="test_type"
                              required
                              value={selectedTestType}
                              onChange={handleTestTypeChange}
                            >
                              <option value="">Select Test Type</option>
                              {testTypes.map((type, i) => (
                                <option key={i} value={type.testType}>
                                  {type.displayName}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <div className="col-xs-12 col-md-6">
                          <div className="feilds">
                            <label className="label">Capture Time</label>
                            <input
                              type="date"
                              data-field="capture_time"
                              max={getTodaysDate()}
                            />
                          </div>
                        </div>
                        <div className="col-xs-12 col-md-6">
                          <div className="feilds">
                            <label className="label">Equipment Status</label>
                            <select data-field="equipment_status">
                              <option value="">Select Status</option>
                              <option value="calibrated">Calibrated</option>
                              <option value="needs_calibration">
                                Needs Calibration
                              </option>
                              <option value="under_maintenance">
                                Under Maintenance
                              </option>
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>

                    {testTemplate && (
                      <>
                        <h4 className="mt-4">
                          Test Results ({testTemplate.displayName})
                        </h4>
                        <div
                          className="patientsFeildsRow"
                          style={{ border: "0", padding: "0" }}
                        >
                          <div className="row">
                            {testTemplate.parameters.map((p, idx) => (
                              <div className="col-xs-12 col-md-6" key={idx}>
                                <div className="feilds mb-3">
                                  <label className="label d-flex justify-content-between">
                                    {p.name}
                                    {p.referenceRange && (
                                      <span
                                        className="text-secondary"
                                        style={{ fontSize: "0.8rem" }}
                                      >
                                        Ref: {p.referenceRange}
                                      </span>
                                    )}
                                  </label>
                                  <div className="d-flex align-items-center gap-2">
                                    {p.inputType === "select" ? (
                                      <select
                                        className="form-control"
                                        value={
                                          dynamicParams[p.name]
                                            ?.parameterValue || ""
                                        }
                                        onChange={(e) =>
                                          handleParamChange(
                                            p.name,
                                            e.target.value,
                                          )
                                        }
                                      >
                                        <option value="">Select</option>
                                        {p.options?.map((opt) => (
                                          <option key={opt} value={opt}>
                                            {opt}
                                          </option>
                                        ))}
                                      </select>
                                    ) : (
                                      <input
                                        type={
                                          p.inputType === "number"
                                            ? "number"
                                            : "text"
                                        }
                                        className="form-control"
                                        value={
                                          dynamicParams[p.name]
                                            ?.parameterValue || ""
                                        }
                                        onChange={(e) =>
                                          handleParamChange(
                                            p.name,
                                            e.target.value,
                                          )
                                        }
                                        placeholder={`Enter ${p.name}`}
                                      />
                                    )}
                                    {p.unit && (
                                      <span className="text-secondary small ms-1">
                                        {p.unit}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </>
                    )}

                    <h4 className="mt-4">Manual Entry & Observations</h4>
                    <div
                      className="patientsFeildsRow"
                      style={{ border: "0", padding: "0" }}
                    >
                      <div className="row">
                        <div className="col-xs-12 col-md-6">
                          <div className="feilds">
                            <label className="label">Sample ID</label>
                            <input
                              type="text"
                              data-field="sample_id"
                              required
                            />
                          </div>
                        </div>
                        <div className="col-xs-12 col-md-6">
                          <div className="feilds">
                            <label className="label">Patient Name</label>
                            <input
                              type="text"
                              value={selectedPatient.name || ""}
                              readOnly
                              data-field="patient_name"
                              required
                            />
                          </div>
                        </div>
                        <div className="col-xs-12 col-md-6">
                          <div className="feilds">
                            <label className="label">Report Date</label>
                            <input
                              type="date"
                              data-field="report_date"
                              max={getTodaysDate()}
                              required
                            />
                          </div>
                        </div>
                        <div className="col-xs-12 col-md-6">
                          <div className="feilds">
                            <label className="label">Report Time</label>
                            <input
                              type="time"
                              data-field="report_time"
                              required
                            />
                          </div>
                        </div>
                        <div className="col-xs-12 col-md-12">
                          <div className="feilds">
                            <label className="label">
                              Additional Observations
                            </label>
                            <textarea
                              rows="4"
                              data-field="additional_observations"
                              required
                              placeholder="Enter additional observations..."
                            ></textarea>
                          </div>
                        </div>
                        <div className="col-xs-12 col-md-12">
                          <div className="feilds">
                            <label className="label">Analyst Name</label>
                            <input
                              type="text"
                              data-field="analyst_name"
                              required
                              placeholder="Enter analyst name..."
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-xs-12 col-md-4">
                    {/* <div className="synBtn text-end">
                      <button className="customeBtn btnLightBlueBorder">
                        Sync : Fetch Equipment Data
                      </button>
                    </div> */}
                  </div>
                </div>
                </fieldset>
              </div>

              <div className="basicDetailsAction">
                <button
                  type="button"
                  className="customeBtn btnEmpty me-2"
                  onClick={handleBackToPatients}
                >
                  {canWriteLab ? "Cancel" : "Back to Patients"}
                </button>
                {!canWriteLab ? (
                  <span className="badge bg-secondary px-3 py-2" style={{ fontSize: "13px" }}>
                    Read-Only Mode ({user?.role_name || "Viewing"})
                  </span>
                ) : (
                  <button
                    type="submit"
                    className="customeBtn btnLightBlue"
                    disabled={loading}
                  >
                    {loading ? "Saving..." : "Save Lab Report"}
                  </button>
                )}
              </div>
            </div>
          </form>

          {/* Lab Reports Successfully saved */}
          {showSuccessModal && (
            <div className="labReportsSuccess">
              <img src={CheckCircle} alt="Success" />
              <h4>Lab Report Successfully Saved!</h4>
              <p>
                Your lab report has been securely recorded and is now available
                for viewing.
              </p>
              <div className="labRepSuccBtn">
                <button
                  className="customeBtn btnLightBlueBorder"
                  onClick={handleCreateNewReport}
                >
                  Create New Report
                </button>
                <button
                  className="customeBtn btnLightBlue"
                  onClick={handleViewReport}
                >
                  View Report
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Toast Container for notifications */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default CreateLabReports;
